/**
 * Lead-list recovery strings (PREC-3536 Phase 5): the grouped Show filter (which also owns
 * the pre-existing `filterOptions` bucket labels, moved here from `enInbox`) and the
 * exclusion-reason sub-row labels. Spread into `enInbox` under the `inbox.` namespace.
 */
export const enListRecovery = {
	filterOptions: {
		BOTH: "All",
		ADDED: "Found",
		NOT_FOUND: "Not found",
		UNSURE: "Unsure",
		BLACKLIST: "Blacklist",
		ARCHIVED: "Archived",
	},
	reasonLabels: {
		jobTitleExcluded: "Job title excluded",
		personNotFound: "Not found on LinkedIn or email",
		neverWorkedHere: "Never worked at the company",
		noLongerThere: "Doesn't work there anymore",
		titleSearchMismatch: "Didn't match the title search",
		countrySearchMismatch: "Didn't match the country search",
		// PREC-3871. contactInfo had no key at all, so the dropdown rendered the raw param
		// ("ContactInfo"); the rest name outcomes that used to be lumped together in "Not found".
		contactInfo: "No contact details found",
		wrongCompany: "Wrong company",
		companyNotFound: "Company could not be identified",
		personNotIdentified: "No person found at the company",
		// PREC-3899: "Find new people only" found only people already in Contacts.
		noNewPerson: "No new person among the top matches",
		icpCompanyMismatch: "Company didn't fit the ICP filter",
		processingError: "Enrichment failed — can be retried",
		processingErrorPermanent: "Enrichment failed — this person's data could not be saved",
		// PREC-4522: the profile could not be read, so the employer was never checked (Unsure).
		employerUnverified: "Employer could not be verified",
		// PREC-4283: an AI company comparison returned an uncertain confidence score (50..74).
		companyMatchUncertain: "Company match uncertain",
		// PREC-4283: matched by name only, and the name is not unique in the customer's lists.
		companyNameAmbiguous: "Company name ambiguous",
		// PREC-4283: the employer is known by name only, and none of the customer's lists has it.
		companyNotIdentified: "Company not identified",
		unreadableFilter: "A list filter could not be read",
		// The child every lead with no recorded reason falls into, so the reason rows always add
		// up to the bucket above them.
		noReason: "Other",
		// PREC-4083: the three blacklist verdicts, so the Blacklist bucket breaks down by why the
		// lead is out rather than by whatever kept it out of the campaign beforehand.
		competitor: "Competitor",
		doNotContact: "On the Do Not Contact list",
		unsubscribed: "Unsubscribed",
		// Blacklisted before the verdict was recorded — the bucket knows they are out, not why.
		blacklistNoReason: "Reason not recorded",
		// PREC-4094: the only reason a Skipped lead ever carries.
		inOtherList: "Already on another list",
		// PREC-4172: a user moved the lead to Unsure by hand. The reason text names who.
		manuallyExcluded: "Excluded manually",
		// PREC-2548: a user took the lead off the blacklist to contact it. The reason text names who.
		includedByUser: "Included by a user",
	},
	// PREC-4172: the "Exclude from list" confirm modal. The question alone read as "exclude" while
	// the lead then showed under "Unsure" with no reason; the customer blamed the AI generation.
	excludeFromListModal: {
		bulkConfirmation: "Exclude {{count}} selected leads from the list?",
		consequence:
			"They move to Unsure, marked as excluded by you, and drop out of campaigns. Undo with Status → To Found.",
	},
	showFilter: {
		show: "Show",
		reasons: "reasons",
		selected: "selected",
	},
	enrichedAt: "Enriched at",
	enrichAnywayFlow: {
		footerButton: "Enrich anyway",
		footerCost: "~{{cost}} credits to enrich",
		modalTitle: "Enrich {{count}} disqualified leads?",
		modalLead:
			"These leads were disqualified by job title. Enriching them now finds their contact details and adds them to your list.",
		costCallout:
			"Already screened — you'll only pay the remaining 0.9 credits per lead ({{total}} credits total), not the full credit.",
		whitelistTitle: "Whitelist these job titles for future lists",
		whitelistDescription:
			"Titles like {{titles}} will be treated as in-persona going forward, so matching contacts are enriched automatically on every future list.",
		timingNote:
			"Enrichment can take 1–2 minutes. Results appear in the list as they finish — or come back later and sort by Enriched at to find them.",
		confirm: "Enrich {{count}} leads",
		cancel: "Cancel",
		progressTitle: "Searching and enriching {{count}} people…",
		progressDescription:
			"This runs in the background and can take a few minutes. We'll update each lead as it's found. To watch results arrive, sort the list so newly enriched leads surface at the top.",
		sortAction: "Sort list by last added/enriched leads",
		keepSorting: "Keep current sorting",
		retryStarted: "Searching this lead again — it updates when the search finishes",
		retryStartedWithOthers: "Searching this lead and {{count}} other people waiting in this list",
		retryOthers: {
			title: "Search other people too?",
			description:
				"Retrying also searches {{count}} other people waiting in this list — each person searched costs {{credits}} credit, also when no one is found.",
			confirm: "Search all",
		},
		// PREC-4520 / PREC-4524: said instead of "started" when nothing was queued, by the API's reason.
		retryNotQueued: {
			ALREADY_SEARCHING:
				"This lead is already being searched — it updates when the search finishes",
			LIST_AT_CAP:
				"This list has reached its lead limit — raise the limit to search this lead again",
			RUN_IN_PROGRESS: "The list is running right now — try again once it has finished",
			LIST_INACTIVE: "The list is paused — resume it to search this lead again",
			NOT_ENOUGH_CREDITS: "Not enough credits to search this lead again",
			NOTHING_TO_RETRY: "Nothing left to search again for this lead — refresh the list",
		},
	},
	reasonTag: {
		titleExcluded: "Title excluded",
		titleExcludedTooltip: "Job title was excluded from enrichment",
		enrichAnyway: "Enrich anyway",
		retry: "Retry",
	},
};
